import { ResourceStats, SelectOption } from "types";
import { RESOURCE_TYPE_STATS } from "./resourcesStatsConstants";

const getLegendVale = (value: string) => {
  if (value === "assessments") {
    return "Assessments";
  } else if (value === "worksheets") {
    return "Worksheets";
  } else if (value === "infosheets") {
    return "Infosheets";
  } else if (value === "linksFiles") {
    return "Links and Files";
  }
};

const getShortMonthName = (monthNumber: number) => {
  const date = new Date(2025, monthNumber, 1);
  return date.toLocaleString("en-US", { month: "short" });
};

const getLongMonthName = (monthNumber: number) => {
  const date = new Date(2025, monthNumber, 1);
  return date.toLocaleString("en-US", { month: "long" });
};

const getDateString = (date: Date | null) => {
  if (!date) return "";
  return `${date.toLocaleString("en-GB", {
    month: "long",
  })} ${date.getDate()}, ${date.getFullYear()}`;
};

const generateStatsData = (
  startDate: Date | null,
  endDate: Date | null,
  selectedResourceType: { value: string; label: string },
  setXLabel: (value: React.SetStateAction<string>) => void,
  resourcesStats: ResourceStats[],
  setChartData: (value: React.SetStateAction<any[]>) => void,
  selectedPatient?: SelectOption[]
) => {
  if (!startDate || !endDate) return;
  let stats = [...resourcesStats];

  if (selectedResourceType.value === "worksheet") {
    stats = resourcesStats.filter((rs) => rs.assignmentType === "WORKSHEET");
  } else if (selectedResourceType.value === "assessment") {
    stats = resourcesStats.filter((rs) => rs.assignmentType === "ASSESSMENT");
  } else if (selectedResourceType.value === "infosheet") {
    stats = resourcesStats.filter((rs) => rs.assignmentType === "INFOSHEET");
  } else if (selectedResourceType.value === "linkFile") {
    stats = resourcesStats.filter(
      (rs) =>
        rs.resourceType === RESOURCE_TYPE_STATS.LINK ||
        rs.resourceType === RESOURCE_TYPE_STATS.FILE
    );
  }

  if (selectedPatient?.length && selectedPatient.length > 0) {
    const patientIds = selectedPatient?.map((p) => p.value);
    stats = stats.filter((rs) => patientIds?.includes(rs.patientId));
  }

  const dateItems = [] as any;

  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const startMonth = startDate?.getMonth();
  const endMonth = endDate?.getMonth();
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();

  let dataObj;
  let dateType: string;

  if (selectedResourceType.value === "all") {
    dataObj = {
      assessments: 0,
      worksheets: 0,
      infosheets: 0,
      linksFiles: 0,
      links: 0,
      files: 0,
    };
  } else if (selectedResourceType.value === "assessment") {
    dataObj = { assessments: 0 };
  } else if (selectedResourceType.value === "worksheet") {
    dataObj = { worksheets: 0 };
  } else if (selectedResourceType.value === "infosheet") {
    dataObj = { infosheets: 0 };
  } else if (selectedResourceType.value === "linkFile") {
    dataObj = { linksFiles: 0, links: 0, files: 0 };
  }

  let startItem;
  let endItem;

  if (endYear - startYear > 0) {
    startItem = startYear;
    endItem = endYear;
    dateType = "year";
  } else if (endMonth - startMonth > 0) {
    startItem = startMonth;
    endItem = endMonth;
    dateType = "month";
  } else {
    startItem = startDay;
    endItem = endDay;
    dateType = "day";
  }

  setXLabel(dateType);

  for (let item = startItem; item <= endItem; item += 1) {
    dateItems.push({
      ...dataObj,
      name: item.toString(),
      dateType,
      startYear,
      startMonth,
      statsObj: {},
    });
  }

  stats.forEach((rs: ResourceStats) => {
    const createdAt = new Date(+rs.createdAt);
    let itemKey: any;

    if (dateType === "year") {
      itemKey = createdAt.getFullYear();
    } else if (dateType === "month") {
      itemKey = createdAt.getMonth();
    } else {
      itemKey = createdAt.getDate();
    }

    const dateItem = dateItems.find(
      (item: any) => item.name === itemKey.toString()
    );
    if (!dateItem) return;

    if (rs.resourceType === "all") {
      switch (rs.assignmentType) {
        case "ASSESSMENT":
          dateItem.assessments = dateItem.assessments + 1;
          break;
        case "WORKSHEET":
          dateItem.worksheets = dateItem.worksheets + 1;
          break;
        case "INFOSHEETS":
          dateItem.infosheets = dateItem.infosheets + 1;
          break;
      }
    } else if (rs.resourceType === RESOURCE_TYPE_STATS.ASSESSMENT) {
      dateItem.assessments = dateItem.assessments + 1;
    } else if (rs.resourceType === RESOURCE_TYPE_STATS.WORKSHEET) {
      dateItem.worksheets = dateItem.worksheets + 1;
    } else if (rs.resourceType === RESOURCE_TYPE_STATS.INFOSHEET) {
      dateItem.infosheets = dateItem.infosheets + 1;
    } else if (rs.resourceType === RESOURCE_TYPE_STATS.LINK) {
      dateItem.linksFiles = dateItem.linksFiles + 1;
      dateItem.links = dateItem.links + 1;
    } else if (rs.resourceType === RESOURCE_TYPE_STATS.FILE) {
      dateItem.linksFiles = dateItem.linksFiles + 1;
      dateItem.files = dateItem.files + 1;
    }

    dateItem.statsObj = { ...rs };
  });

  if (dateType === "month") {
    const data = dateItems.map((item: any) => ({
      ...item,
      name: getShortMonthName(item.name),
    }));
    setChartData(data);
  } else {
    setChartData([...dateItems]);
  }
};

function getFullMonthNameFromShort(shortMonth: string, locale = "en-US") {
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => {
    const date = new Date(2000, i, 1);
    return {
      short: new Intl.DateTimeFormat(locale, { month: "short" }).format(date),
      long: new Intl.DateTimeFormat(locale, { month: "long" }).format(date),
    };
  });

  const match = months.find(
    (m) => m.short.toLowerCase() === shortMonth.toLowerCase()
  );
  return match?.long || null;
}

const getFormattedDateTooltip = (
  label: string,
  dateType: "year" | "month" | "day",
  year: string,
  month: number
) => {
  if (dateType === "month") {
    const month = getFullMonthNameFromShort(label);
    return month + " " + year;
  }
  if (dateType === "day") {
    const monthLong = getLongMonthName(month);
    return monthLong + " " + label + ", " + year;
  }
  return label;
};

export {
  getLegendVale,
  getLongMonthName,
  getShortMonthName,
  getDateString,
  generateStatsData,
  getFormattedDateTooltip,
  getFullMonthNameFromShort,
};
